import { postBillingOnce as sendBillingOnce } from '../lib/billingRequests';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Globale Rechte-Fehler-Behandlung (Q4c: Toast bei Kein Zugriff)
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 403) {
      const detail = err.response.data?.detail;
      const perm = err.response.data?.required_permission;
      if (typeof detail === 'string' && (detail.includes('Berechtigung') || detail.includes('Zugriff'))) {
        // Lazy toast import to avoid circular deps
        import('sonner').then(({ toast }) => {
          toast.error('Kein Zugriff', {
            description: perm ? `Erforderliches Recht: ${perm}` : detail,
            duration: 5000,
          });
        });
      }
    }
    return Promise.reject(err);
  }
);

// Error helper
export const formatApiError = (error) => {
  const detail = error.response?.data?.detail;
  if (detail == null) return 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

// Auth
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  register: (data) => api.post('/auth/register', data)
};

// Vehicles
export const vehicleApi = {
  getAll: (params) => api.get('/vehicles', { params }),
  getOne: (id) => api.get(`/vehicles/${id}`),
  create: (data) => api.post('/vehicles', data),
  update: (id, data) => api.put(`/vehicles/${id}`, data),
  delete: (id) => api.delete(`/vehicles/${id}`),
  uploadImage: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/vehicles/${id}/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadRegistrationDocument: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/vehicles/${id}/registration-document`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteRegistrationDocument: (id) =>
    api.delete(`/vehicles/${id}/registration-document`)
};

// Customers
export const customerApi = {
  getAll: (params) => api.get('/customers', { params }),
  getOne: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  createQuick: (data) => api.post('/customers/quick', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  getStatementPdf: (id, params = {}) => api.get(`/customers/${id}/statement-pdf`, { responseType: 'blob', params }),
  // Documents / profile image
  uploadDocument: (id, docType, file, name) => {
    const fd = new FormData();
    fd.append('file', file);
    if (name) fd.append('name', name);
    return api.post(`/customers/${id}/upload?doc_type=${encodeURIComponent(docType)}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  documentUrl: (id, docType, docId) => {
    const url = `${API}/api/customers/${id}/documents/${docType}`;
    return docId ? `${url}?doc_id=${docId}` : url;
  },
  deleteDocument: (id, docType, docId) => {
    const params = docId ? { doc_id: docId } : {};
    return api.delete(`/customers/${id}/documents/${docType}`, { params });
  },
  extractFace: (id, docType = 'id_front') => api.post(`/customers/${id}/extract-face?doc_type=${docType}`),
};

// Bookings
export const bookingApi = {
  getAll: (params) => api.get('/bookings', { params }),
  getOne: (id) => api.get(`/bookings/${id}`),
  create: (data) => api.post('/bookings', data),
  update: (id, data) => api.put(`/bookings/${id}`, data),
  delete: (id) => api.delete(`/bookings/${id}`),
  previewChange: (id, data) => api.post(`/bookings/${id}/change-preview`, data),
  checkIn: (id, data) => api.post(`/bookings/${id}/check-in`, data),
  checkOut: (id, data) => api.post(`/bookings/${id}/check-out`, data),
  undoCheckIn: (id) => api.post(`/bookings/${id}/check-in/undo`),
  undoCheckOut: (id) => api.post(`/bookings/${id}/check-out/undo`),
  uploadPhoto: (id, phase, file, slot = null) => {
    const fd = new FormData();
    fd.append('file', file);
    const slotParam = slot ? `&slot=${encodeURIComponent(slot)}` : '';
    return api.post(`/bookings/${id}/photos?phase=${phase}${slotParam}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deletePhoto: (id, phase, { slot, path } = {}) => {
    const params = new URLSearchParams({ phase });
    if (slot) params.set('slot', slot);
    if (path) params.set('path', path);
    return api.delete(`/bookings/${id}/photos?${params.toString()}`);
  },
  getAccidentReportPdf: (id) => api.get(`/bookings/${id}/accident-report/pdf`, { responseType: 'blob' }),
  getProtocolPdf: (id) => api.get(`/bookings/${id}/protocol/pdf`, { responseType: 'blob' }),
  sendConfirmation: (id) => api.post(`/bookings/${id}/send-confirmation`),
  sendRegistrationDocumentEmail: (id) => api.post(`/bookings/${id}/registration-document/send-email`),
  // Kilometer-Pauschale
  getKmUsage: (id) => api.get(`/bookings/${id}/km-usage`),
  createExtraKmCharge: (id, data = {}) => api.post(`/bookings/${id}/extra-km-charge`, data),
  // Fahrzeug-Tausch während der Miete (Defekt etc.)
  getSwapCandidates: (id) => api.get(`/bookings/${id}/swap-candidates`),
  swapVehicle: (id, data) => api.post(`/bookings/${id}/swap-vehicle`, data),
  getSwapsOverview: (id) => api.get(`/bookings/${id}/swaps`),
  getSwapPhotoSlots: (id) => api.get(`/bookings/${id}/swaps/photo-slots`),
  uploadSwapPhoto: (bookingId, swapId, phase, slotKey, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/bookings/${bookingId}/swaps/${swapId}/photos`, fd, {
      params: { phase, slot_key: slotKey },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteSwapPhoto: (bookingId, swapId, photoId) =>
    api.delete(`/bookings/${bookingId}/swaps/${swapId}/photos/${photoId}`),
  swapPhotoUrl: (bookingId, swapId, photoId) =>
    `${API}/api/bookings/${bookingId}/swaps/${swapId}/photos/${photoId}/raw`,
  swapContractPdfUrl: (bookingId, swapId) =>
    `${API}/api/bookings/${bookingId}/swaps/${swapId}/contract-pdf`,
  // Fristlose Kündigung (AGB-Verstoß)
  terminate: (id, data) => api.post(`/bookings/${id}/terminate`, data),
  getTerminationPdf: (id) => api.get(`/bookings/${id}/termination-pdf`, { responseType: 'blob' }),
  sendTerminationEmail: (id) => api.post(`/bookings/${id}/termination/send-email`),
};

const postBillingOnce = (path, data = {}) => sendBillingOnce(api, path, data);

// Invoices
export const invoiceApi = {
  getAll: (params) => api.get('/invoices', { params }),
  getOne: (id) => api.get(`/invoices/${id}`),
  create: (data) => postBillingOnce('/invoices', data),
  createFree: (data) => postBillingOnce('/invoices/free', data),
  recordPayment: (id, data) => postBillingOnce(`/invoices/${id}/payment`, data),
  getPdf: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  sendEmail: (id) => api.post(`/invoices/${id}/send-email`),
  getOverdue: () => api.get('/invoices/overdue'),
  sendReminder: (id) => api.post(`/invoices/${id}/send-reminder`),
  getReminderPdf: (id) => api.get(`/invoices/${id}/reminder-pdf`, { responseType: 'blob' }),
  cancel: (id) => postBillingOnce(`/invoices/${id}/cancel`),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  createCreditNote: (id, data) => postBillingOnce(`/invoices/${id}/credit-note`, data),
};

// Credit Notes / Gutschriften
export const creditNoteApi = {
  list: (params) => api.get('/credit-notes', { params }),
  create: (data) => api.post('/credit-notes', data),
  getPdf: (id) => api.get(`/credit-notes/${id}/pdf`, { responseType: 'blob' }),
  update: (id, data) => api.put(`/credit-notes/${id}`, data),
  delete: (id) => api.delete(`/credit-notes/${id}`),
};

// Deposits (Kautionen)
export const depositApi = {
  getAll: (params) => api.get('/deposits', { params }),
  create: (data) => api.post('/deposits', data),
  return: (id, payment_method) => api.put(`/deposits/${id}/return`, payment_method ? { payment_method } : {}),
  delete: (id) => api.delete(`/deposits/${id}`),

  overview: () => api.get('/deposits/overview'),
  markReview: (id) => api.post(`/deposits/${id}/mark-review`),
  settle: (id, data) => api.post(`/deposits/${id}/settle`, data),
  invoiceOptions: (id) => api.get(`/deposits/${id}/invoice-options`),
  paymentOptions: (invoiceId) => api.get(`/invoices/${invoiceId}/deposit-options`),
  applyToInvoice: (id, data) => api.post(`/deposits/${id}/apply-to-invoice`, data),
  completeRefund: (id, data) => api.post(`/deposits/${id}/complete-refund`, data),
  getStatementPdf: (id) => api.get(`/deposits/${id}/statement-pdf`, {
    responseType: 'blob'
  })
};

// Receipts (Quittungen)
export const receiptApi = {
  getAll: () => api.get('/receipts'),
  create: (data) => api.post('/receipts', data),
  getPdf: (id) => api.get(`/receipts/${id}/pdf`, { responseType: 'blob' })
};

// Contracts
export const contractApi = {
  getPdf: (bookingId) => api.get(`/contracts/${bookingId}/pdf`, { responseType: 'blob' })
};

// Quotes (Angebote)
export const quoteApi = {
  getAll: (params) => api.get('/quotes', { params }),
  create: (data) => api.post('/quotes', data),
  accept: (id) => api.put(`/quotes/${id}/accept`),
  reject: (id) => api.put(`/quotes/${id}/reject`),
  update: (id, data) => api.put(`/quotes/${id}`, data),
  convertToBooking: (id) => api.post(`/quotes/${id}/convert-to-booking`),
  delete: (id) => api.delete(`/quotes/${id}`),
  getPdf: (id) => api.get(`/quotes/${id}/pdf`, { responseType: 'blob' })
};

// Calendar & Availability
export const calendarApi = {
  getAvailability: (startDate, endDate, vehicleId) => 
    api.get('/availability', { params: { start_date: startDate, end_date: endDate, vehicle_id: vehicleId } }),
  getCalendar: (month, year) => api.get('/calendar', { params: { month, year } })
};

// Dashboard
export const dashboardApi = {
  getStats: (month) => api.get('/dashboard/stats', { params: { month } }),
  getCharts: (months = 6) => api.get('/dashboard/charts', { params: { months } }),
  getQuickActions: () => api.get('/dashboard/quick-actions'),
};

// Settings
export const settingsApi = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
  getAutoLetterRules: () => api.get('/settings/auto-letter-rules'),
  updateAutoLetterRules: (rules) => api.put('/settings/auto-letter-rules', { rules }),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/settings/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Nummernkreise (entity number formats)
  getNumberFormats: () => api.get('/settings/number-formats'),
  updateNumberFormats: (formats) => api.put('/settings/number-formats', formats),
  // Foto-Slots Übergabe/Rückgabe
  getPhotoSlots: () => api.get('/settings/photo-slots'),
  updatePhotoSlots: (slots) => api.put('/settings/photo-slots', slots),
};

// Schadenskatalog (damage catalog)
export const damageCatalogApi = {
  getAll: (params = {}) => api.get('/damage-catalog', { params }),
  create: (data) => api.post('/damage-catalog', data),
  update: (id, data) => api.put(`/damage-catalog/${id}`, data),
  delete: (id) => api.delete(`/damage-catalog/${id}`),
};

// Interne Fahrzeug-Belegungen (vehicle blocks)
export const blockApi = {
  getAll: (params = {}) => api.get('/blocks', { params }),
  create: (data) => api.post('/blocks', data),
  update: (id, data) => api.put(`/blocks/${id}`, data),
  delete: (id) => api.delete(`/blocks/${id}`),
  attachInvoice: (id, formData) =>
    api.post(`/blocks/${id}/invoice`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  removeInvoice: (id) => api.delete(`/blocks/${id}/invoice`),
};

// QR-Codes für Fahrzeuge
export const qrApi = {
  vehiclePngUrl: (vehicleId, size = 8) =>
    `${API}/api/vehicles/${vehicleId}/qrcode.png?size=${size}`,
  vehicleLabelPdfUrl: (vehicleId, format = 'sticker') =>
    `${API}/api/vehicles/${vehicleId}/label.pdf?format=${format}`,
  allLabelsPdfUrl: (format = 'sticker') =>
    `${API}/api/qr/all-labels.pdf?format=${format}`,
  resolve: (token) => api.get(`/qr/resolve/${encodeURIComponent(token)}`),
};

export const vehicleMileageApi = {
  update: (id, current_mileage) =>
    api.post(`/vehicles/${id}/mileage`, { current_mileage }),
};

// Stations
export const stationApi = {
  getAll: () => api.get('/stations'),
  create: (data) => api.post('/stations', data),
  update: (id, data) => api.put(`/stations/${id}`, data),
  delete: (id) => api.delete(`/stations/${id}`)
};

// Categories
export const categoryApi = {
  getAll: () => api.get('/categories'),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`)
};

// Damages (Schadensmanagement)
export const damageApi = {
  getAll: (params) => api.get('/damages', { params }),
  getOne: (id) => api.get(`/damages/${id}`),
  getStats: () => api.get('/damages/stats'),
  create: (data) => api.post('/damages', data),
  update: (id, data) => api.put(`/damages/${id}`, data),
  delete: (id) => api.delete(`/damages/${id}`),
  uploadPhoto: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/damages/${id}/photos`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  createInvoice: (id) => api.post(`/damages/${id}/invoice`),
};

// Cashbook (Kassenbuch)
export const cashbookApi = {
  list: (params) => api.get('/cashbook', { params }),
  summary: () => api.get('/cashbook/summary'),
  categories: () => api.get('/cashbook/categories'),
  suppliers: () => api.get('/cashbook/suppliers'),
  create: (data) => api.post('/cashbook', data),
  update: (id, data) => api.put(`/cashbook/${id}`, data),
  delete: (id) => api.delete(`/cashbook/${id}`),
  exportPdf: (year, month) => api.get(`/cashbook/export/pdf?year=${year}&month=${month}`, { responseType: 'blob' }),
  exportCsv: (year, month) => api.get(`/cashbook/export/csv?year=${year}&month=${month}`, { responseType: 'blob' }),
  // Tagesabschluss / Kassensturz
  dailyClosing: (date) => api.get('/cashbook/daily-closing', { params: { date } }),
  dailyClosingPdf: (date, countedCash, notes) =>
    api.get('/cashbook/daily-closing/pdf', {
      params: { date, counted_cash: countedCash, notes },
      responseType: 'blob',
    }),
};

// Payments
export const paymentApi = {
  // Booking-based checkout (existing): pays invoice for a booking
  createCheckout: (bookingId, origin) =>
    api.post(`/payments/checkout?booking_id=${bookingId}`, {}, {
      headers: { Origin: origin }
    }),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  // Extended Stripe flows
  checkoutInvoice: (invoiceId) =>
    api.post(`/payments/checkout/invoice/${invoiceId}`, { origin_url: window.location.origin }),
  checkoutDeposit: (bookingId) =>
    api.post(`/payments/checkout/deposit/${bookingId}`, { origin_url: window.location.origin }),
  statusExtended: (sessionId) => api.get(`/payments/checkout/status/${sessionId}`),
  list: () => api.get('/payments'),
};

// Users
export const userApi = {
  getAll: () => api.get('/users'),
  delete: (id) => api.delete(`/users/${id}`)
};

// Inquiries (Homepage-Anfragen)
export const inquiryApi = {
  list: (params) => api.get('/inquiries', { params }),
  stats: () => api.get('/inquiries/stats'),
  get: (id) => api.get(`/inquiries/${id}`),
  create: (data) => api.post('/inquiries', data),
  update: (id, data) => api.put(`/inquiries/${id}`, data),
  convert: (id) => api.post(`/inquiries/${id}/convert`),
  delete: (id) => api.delete(`/inquiries/${id}`),
  regenerateApiKey: () => api.post('/settings/api-key/regenerate'),
  // Inbox / Reply thread
  fetchEmails: () => api.post('/inquiries/fetch-emails'),
  getMessages: (id) => api.get(`/inquiries/${id}/messages`),
  reply: (id, body, subject, files) => {
    const fd = new FormData();
    fd.append('body', body || '');
    if (subject) fd.append('subject', subject);
    (files || []).forEach((f) => fd.append('files', f));
    return api.post(`/inquiries/${id}/reply`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  attachmentUrl: (id) => `${API}/api/inquiries/attachments/${id}`,
};

// E-Mail configuration (IMAP / SMTP)
export const emailConfigApi = {
  get: () => api.get('/settings/email'),
  update: (data) => api.put('/settings/email', data),
  test: () => api.post('/settings/email/test'),
};

// E-Mail Templates
export const emailTemplateApi = {
  list: () => api.get('/email-templates'),
  placeholders: () => api.get('/email-templates/placeholders'),
  create: (data) => api.post('/email-templates', data),
  update: (id, data) => api.put(`/email-templates/${id}`, data),
  delete: (id) => api.delete(`/email-templates/${id}`),
  render: (id, inquiryId) => api.get(`/email-templates/${id}/render`, { params: inquiryId ? { inquiry_id: inquiryId } : {} }),
};

// Export
export const exportApi = {
  vehicles: () => `${API}/api/export/vehicles`,
  customers: () => `${API}/api/export/customers`,
  bookings: () => `${API}/api/export/bookings`,
  invoices: () => `${API}/api/export/invoices`,
  revenueReport: (month, year) => `${API}/api/export/revenue-report?month=${month}&year=${year}`,
  // PDF variants
  pdfVehicles: () => `${API}/api/export/pdf/vehicles`,
  pdfCustomers: () => `${API}/api/export/pdf/customers`,
  pdfBookings: () => `${API}/api/export/pdf/bookings`,
  pdfInvoices: () => `${API}/api/export/pdf/invoices`,
  pdfRevenueReport: (month, year) => `${API}/api/export/pdf/revenue-report?month=${month}&year=${year}`,
  // Vehicle revenue (per Fahrzeug × Monat)
  vehicleRevenue: (from, to, vehicleId) => {
    const p = new URLSearchParams({ from, to });
    if (vehicleId) p.set('vehicle_id', vehicleId);
    return `${API}/api/reports/vehicle-revenue?${p.toString()}`;
  },
  pdfVehicleRevenue: (from, to, vehicleId, includeDetails = true) => {
    const p = new URLSearchParams({ from, to, include_details: String(includeDetails) });
    if (vehicleId) p.set('vehicle_id', vehicleId);
    return `${API}/api/export/pdf/vehicle-revenue?${p.toString()}`;
  },
};

// Vehicle revenue API (returns JSON)
export const vehicleRevenueApi = {
  get: (from, to, vehicleId) => api.get('/reports/vehicle-revenue', { params: { from, to, ...(vehicleId ? { vehicle_id: vehicleId } : {}) } }),
};

// Fahrzeug-Kostenanalyse (Werkstattrechnungen + Schäden vs. Umsatz)
export const vehicleCostApi = {
  get: (from, to, vehicleId) => api.get('/reports/vehicle-cost-analysis', { params: { from, to, ...(vehicleId ? { vehicle_id: vehicleId } : {}) } }),
};

// Maintenance (Wartungscenter)
export const maintenanceApi = {
  list: (params) => api.get('/maintenance', { params }),
  upcoming: (days = 30) => api.get('/maintenance/upcoming', { params: { days } }),
  create: (data) => api.post('/maintenance', data),
  update: (id, data) => api.put(`/maintenance/${id}`, data),
  remove: (id) => api.delete(`/maintenance/${id}`),
  uploadAttachment: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/maintenance/${id}/attachments`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteAttachment: (id, fileId) => api.delete(`/maintenance/${id}/attachments/${fileId}`),
};

export const tireApi = {
  reminders: (params = {}) => api.get('/tires/reminders', { params }),
  recordChange: (vehicleId, data) => api.post(`/vehicles/${vehicleId}/tire-change`, data),
  notifyCustomer: (vehicleId) => api.post(`/vehicles/${vehicleId}/tire-notify`),
};

export const dunningApi = {
  overview: () => api.get('/dunning/overview'),
  stats: () => api.get('/dunning/stats'),
  sendReminder: (invoiceId) => api.post(`/invoices/${invoiceId}/send-reminder`),
  toggleInterest: (invoiceId, enabled) => api.put(`/invoices/${invoiceId}/dunning-interest`, null, { params: { enabled } }),
  reminderPdfUrl: (invoiceId) => `${API}/api/invoices/${invoiceId}/reminder-pdf`,
};

export const paymentPlanApi = {
  createAuto: (invoiceId, { months, first_due_date, notes } = {}) =>
    api.post(`/invoices/${invoiceId}/payment-plan/auto`, { months, first_due_date, notes }),
  createCustom: (invoiceId, { installments, notes } = {}) =>
    api.post(`/invoices/${invoiceId}/payment-plan`, { installments, notes }),
  cancel: (invoiceId) => api.delete(`/invoices/${invoiceId}/payment-plan`),
  payInstallment: (invoiceId, n, { amount, method } = {}) =>
    postBillingOnce(`/invoices/${invoiceId}/payment-plan/installments/${n}/pay`, { amount, method }),
};

// Fahrzeug-Reinigung
export const cleaningApi = {
  getForVehicle: (vehicleId) => api.get(`/vehicles/${vehicleId}/cleaning`),
  start: (vehicleId) => api.post(`/vehicles/${vehicleId}/cleaning/start`),
  toggleItem: (vehicleId, key, data) => api.post(`/vehicles/${vehicleId}/cleaning/items/${key}`, data),
  complete: (vehicleId) => api.post(`/vehicles/${vehicleId}/cleaning/complete`),
  skip: (vehicleId) => api.post(`/vehicles/${vehicleId}/cleaning/skip`),
  getTemplate: () => api.get('/cleaning/checklist-template'),
  saveTemplate: (items) => api.put('/cleaning/checklist-template', { items }),
  dashboardStats: (overdueHours) => api.get('/cleaning/dashboard-stats', { params: overdueHours ? { overdue_hours: overdueHours } : {} }),
};

// Posteingang / Inbox
export const inboxApi = {
  list: (params = {}) => api.get('/inbox', { params }),
  stats: () => api.get('/inbox/stats'),
  get: (id) => api.get(`/inbox/${id}`),
  create: (formData) => api.post('/inbox', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, data) => api.put(`/inbox/${id}`, data),
  remove: (id) => api.delete(`/inbox/${id}`),
  attachFile: (id, formData) => api.post(`/inbox/${id}/files`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  removeFile: (id, fileId) => api.delete(`/inbox/${id}/files/${fileId}`),
  fileUrl: (id, fileId) => `${API}/api/inbox/${id}/files/${fileId}/raw`,
  analyze: (id) => api.post(`/inbox/${id}/analyze`),
  markDone: (id) => api.post(`/inbox/${id}/mark-done`),
  archive: (id) => api.post(`/inbox/${id}/archive`),
  reopen: (id) => api.post(`/inbox/${id}/reopen`),
  createReply: (id) => api.post(`/inbox/${id}/create-reply`),
  categories: () => api.get('/inbox/categories'),
  addCategory: (data) => api.post('/inbox/categories', data),
  removeCategory: (key) => api.delete(`/inbox/categories/${key}`),
};

export const datevApi = {
  preview: (params) => api.get('/datev/preview', { params }),
  csvUrl: (params) => `${API}/api/datev/export?${new URLSearchParams({ ...params, format: 'csv' }).toString()}`,
  pdfUrl: (params) => `${API}/api/datev/export?${new URLSearchParams({ ...params, format: 'pdf' }).toString()}`,
};

// Strafzettel / Traffic Tickets
export const ticketApi = {
  getAll: (params = {}) => api.get('/tickets', { params }),
  get: (id) => api.get(`/tickets/${id}`),
  create: (data) => api.post('/tickets', data),
  update: (id, data) => api.put(`/tickets/${id}`, data),
  remove: (id) => api.delete(`/tickets/${id}`),
  notifyCustomer: (id) => api.post(`/tickets/${id}/notify-customer`),
  chargeCustomer: (id) => api.post(`/tickets/${id}/charge-customer`),
  stats: (year) => api.get('/tickets/reports/stats', { params: year ? { year } : {} }),
  reminders: () => api.get('/tickets/reminders'),
  halterauskunftUrl: (id) => `${API}/api/tickets/${id}/halterauskunft.pdf`,
  uploadAttachment: (id, formData) => api.post(`/tickets/${id}/attachments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteAttachment: (id, fileId) => api.delete(`/tickets/${id}/attachments/${fileId}`),
  fileUrl: (storagePath) => `${API}/api/files/${storagePath}`,
  extractFromAttachment: (id, fileId) => api.post(`/tickets/${id}/attachments/${fileId}/extract`),
};

// Lieferanten (Suppliers)
export const supplierApi = {
  getAll: (activeOnly = false) => api.get('/suppliers', { params: { active_only: activeOnly } }),
  get: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  remove: (id) => api.delete(`/suppliers/${id}`),
};

// Lieferanten-Abrechnungen (Settlements / Gutschriftverfahren)
export const settlementApi = {
  preview: (params) => api.get('/settlements/preview', { params }),
  create: (data) => api.post('/settlements', data),
  list: (params = {}) => api.get('/settlements', { params }),
  get: (id) => api.get(`/settlements/${id}`),
  remove: (id) => api.delete(`/settlements/${id}`),
  pdfUrl: (id) => `${API}/api/settlements/${id}/pdf`,
};

// Kunden-Onboarding (Admin-Inbox)
export const onboardingApi = {
  list: (params = {}) => api.get('/customer-onboardings', { params }),
  get: (id) => api.get(`/customer-onboardings/${id}`),
  createInvitation: (data = {}) => api.post('/customer-onboardings/invitations', data),
  approve: (id, data = {}) => api.post(`/customer-onboardings/${id}/approve`, data),
  reject: (id, data = {}) => api.post(`/customer-onboardings/${id}/reject`, data),
  remove: (id) => api.delete(`/customer-onboardings/${id}`),
  adminDocUrl: (id, docType) => `${API}/api/customer-onboardings/${id}/document/${docType}`,
};

// Public Onboarding (kein Auth, Token = Zugang)
export const publicOnboardingApi = {
  startPublic: () => api.post('/onboarding/public/start'),
  get: (token) => api.get(`/onboarding/${token}`),
  update: (token, data) => api.put(`/onboarding/${token}`, data),
  upload: (token, docType, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/onboarding/${token}/upload/${docType}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteDoc: (token, docType) => api.delete(`/onboarding/${token}/upload/${docType}`),
  submit: (token) => api.post(`/onboarding/${token}/submit`),
  docUrl: (token, docType) => `${API}/api/onboarding/${token}/document/${docType}`,
};

// Briefe / Korrespondenz
export const letterApi = {
  list: (params = {}) => api.get('/letters', { params }),
  get: (id) => api.get(`/letters/${id}`),
  create: (data) => api.post('/letters', data),
  update: (id, data) => api.put(`/letters/${id}`, data),
  remove: (id) => api.delete(`/letters/${id}`),
  pdf: (id) => api.get(`/letters/${id}/pdf`, { responseType: 'blob' }),
  pdfUrl: (id) => `${API}/api/letters/${id}/pdf`,
  sendEmail: (id) => api.post(`/letters/${id}/send-email`),
  archive: (id) => api.post(`/letters/${id}/archive`),
  aiDraft: (data) => api.post('/letters/ai-draft', data),
};

export const letterTemplateApi = {
  list: (params = {}) => api.get('/letter-templates', { params }),
  create: (data) => api.post('/letter-templates', data),
  update: (id, data) => api.put(`/letter-templates/${id}`, data),
  remove: (id) => api.delete(`/letter-templates/${id}`),
};

export const longTermApi = {
  list: (params = {}) => api.get('/long-term-contracts', { params }),
  get: (id) => api.get(`/long-term-contracts/${id}`),
  create: (data) => api.post('/long-term-contracts', data),
  update: (id, data) => api.put(`/long-term-contracts/${id}`, data),
  remove: (id) => api.delete(`/long-term-contracts/${id}`),
  activate: (id, data = {}) => api.post(`/long-term-contracts/${id}/activate`, data),
  terminate: (id, data) => api.post(`/long-term-contracts/${id}/terminate`, data),
  addMileage: (id, data) => api.post(`/long-term-contracts/${id}/mileage-readings`, data),
  listMileage: (id) => api.get(`/long-term-contracts/${id}/mileage-readings`),
  generateInvoice: (id, data = {}) => postBillingOnce(`/long-term-contracts/${id}/generate-invoice`, data),
  pdfUrl: (id) => `${API}/api/long-term-contracts/${id}/pdf`,
  stats: () => api.get('/long-term-contracts/stats/overview'),
};

export const portalApi = {
  // Auth
  login: (email, password) => api.post('/portal/auth/login', { email, password }),
  logout: () => api.post('/portal/auth/logout'),
  setup: (token, password) => api.post('/portal/auth/setup', { token, password }),
  verifySetup: (token) => api.get(`/portal/auth/verify-setup/${token}`),
  changePassword: (data) => api.post('/portal/auth/change-password', data),
  requestMagicLink: (email) => api.post('/portal/auth/request-magic-link', { email }),
  me: () => api.get('/portal/me'),
  // Data
  dashboard: () => api.get('/portal/dashboard'),
  longTerm: () => api.get('/portal/long-term-contracts'),
  longTermDetail: (id) => api.get(`/portal/long-term-contracts/${id}`),
  bookings: () => api.get('/portal/bookings'),
  accidentReportUrl: (id) => `${API}/api/portal/bookings/${encodeURIComponent(id)}/accident-report/pdf`,
  invoices: () => api.get('/portal/invoices'),
  invoicePdfUrl: (id) => `${API}/api/portal/invoices/${id}/pdf`,
  damages: () => api.get('/portal/damages'),
  tickets: () => api.get('/portal/tickets'),
};

export const portalAdminApi = {
  sendMagicLink: (customerId) => api.post(`/portal/admin/customers/${customerId}/magic-link`),
  status: (customerId) => api.get(`/portal/admin/customers/${customerId}/portal-status`),
  revoke: (customerId) => api.post(`/portal/admin/customers/${customerId}/revoke`),
};

export default api;

export const whatsappApi = {
  status: () => api.get('/whatsapp/status'),
  templates: () => api.get('/whatsapp/templates'),
  threads: () => api.get('/whatsapp/threads'),
  thread: (customer_id) => api.get(`/whatsapp/thread/${customer_id}`),
  send: (data) => api.post('/whatsapp/send', data),
  getConfig: () => api.get('/whatsapp/config'),
  updateConfig: (data) => api.put('/whatsapp/config', data),
  disconnect: () => api.delete('/whatsapp/config'),
  verify: () => api.post('/whatsapp/verify'),
  test: (data) => api.post('/whatsapp/test', data),
};


// E-Mail Client
export const emailApi = {
  getMessages: (limit = 100, params = {}) => api.get('/emails', { params: { limit, ...params } }),
  saveDraft: data => api.post('/emails/drafts', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteDraft: uid => api.delete(`/emails/${uid}`),
  link: (uid, data) => api.put(`/emails/${uid}/links`, data),
  getMessage: (uid) => api.get(`/emails/${encodeURIComponent(uid)}`),
  attachmentUrl: (uid, attachmentId) => `${API}/api/emails/${encodeURIComponent(uid)}/attachments/${attachmentId}`,

  send: (formData) => api.post('/emails/send', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
};


export const companyDocumentApi = {
  reminders: () => api.get('/company-documents/reminders'),
  versions: (id) => api.get(`/company-documents/${id}/versions`),
  version: (id, version) => api.get(`/company-documents/${id}/versions/${version}`),
  restore: (id, version, expected) => api.post(`/company-documents/${id}/restore/${version}`, null, { params: { expected_version: expected } }),
  replaceFile: (id, data) => api.post(`/company-documents/${id}/replace-file`, data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  versionFileUrl: (id, version) => `${API}/api/company-documents/${id}/versions/${version}/file`,
  list: (params) => api.get('/company-documents', { params }),
  categories: () => api.get('/company-documents/categories'),
  get: (id) => api.get(`/company-documents/${encodeURIComponent(id)}`),
  create: (data) => api.post('/company-documents', data),
  update: (id, data) => api.put(`/company-documents/${encodeURIComponent(id)}`, data),
  remove: (id) => api.delete(`/company-documents/${encodeURIComponent(id)}`),
  upload: (data) => api.post('/company-documents/upload', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  aiDraft: (data) => api.post('/company-documents/ai-draft', data),
  fileUrl: (id, download = false) => `${API}/api/company-documents/${encodeURIComponent(id)}/file?download=${download}`,
  pdfUrl: (id) => `${API}/api/company-documents/${encodeURIComponent(id)}/pdf`,
};

export const accidentApi = (bookingId = null) => {
  const base = bookingId ? `/portal/bookings/${encodeURIComponent(bookingId)}/accidents` : '/accidents';
  return {list:(params)=>api.get(base,{params}),create:()=>api.post(base),save:(id,data)=>api.put(`${base}/${id}`,data),submit:(id,data)=>api.post(`${base}/${id}/submit`,data),upload:(id,data)=>api.post(`${base}/${id}/photos`,data,{headers:{'Content-Type':'multipart/form-data'}}),removePhoto:(id,photo,version)=>api.delete(`${base}/${id}/photos/${photo}`,{params:{version}}),photoUrl:(id,photo)=>`${API}/api${base}/${id}/photos/${photo}`,pdfUrl:id=>`${API}/api${base}/${id}/pdf`,review:id=>api.post(`${base}/${id}/review`)};
};
